<template>
  <v-card>
    <v-card-text>
      <v-subheader>Chapel Credits: {{ term }}</v-subheader>
      <v-row>
        <v-col>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle>Required</v-list-item-subtitle>
              <v-list-item-title>{{ required }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-col>
        <v-col>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle>Received</v-list-item-subtitle>
              <v-list-item-title>{{ received }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-col>
        <v-col>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle>Remaining</v-list-item-subtitle>
              <v-list-item-title>{{ remaining }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-col>
      </v-row>
      <template v-if="missed > 0">
        <v-subheader>Missed Chapels & Makeup Credits
          <v-dialog v-model="missedDialog" width="600">
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon class="ml-4">
                <v-icon color="info">fas fa-question-circle</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title>Makeup Credits</v-card-title>
              <v-card-text>
                <p>If you miss one or more chapels, you can makeup the credit by going to a makeup chapel. You have currently missed {{ missed }} regular chapels, and have received {{ makeups }} credit{{ makeups === 1 ? '' : 's' }}, so you are currently able to recieve {{ makeupsAvailable }} credit{{ makeupsAvailable === 1 ? '' : 's' }} from makeup chapels.</p>
                <p v-if="nextMakeup">The next makeup session is {{ stringFormatDate(nextMakeup) }}.</p>
                <p v-else>There are currently no makeup sessions scheduled.</p>
              </v-card-text>
              <v-card-actions>
                <v-btn @click="missedDialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-subheader>
        <v-row>
          <v-col :cols="12" :sm="6" :md="3">
            <v-list-item class="pr-0">
              <v-list-item-content>
                <v-list-item-subtitle>Missed</v-list-item-subtitle>
                <v-list-item-title>{{ missed }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col :cols="12" :sm="6" :md="3">
            <v-list-item class="pa-0">
              <v-list-item-content>
                <v-list-item-subtitle>Credits</v-list-item-subtitle>
                <v-list-item-title>{{ makeups }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col :cols="12" :sm="6" :md="3">
            <v-list-item class="pa-0">
              <v-list-item-content>
                <v-list-item-subtitle>Available</v-list-item-subtitle>
                <v-list-item-title>{{ makeupsAvailable }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col :cols="12" :sm="6" :md="3">
            <v-list-item class="pa-0">
              <v-list-item-content>
                <v-list-item-subtitle>Next Makeup</v-list-item-subtitle>
                <v-list-item-title>{{ nextMakeup || 'Unscheduled' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-col>
        </v-row>
      </template>
    </v-card-text>
    <v-progress-linear :value="required > 0 ? (received / required) * 100 : 100" />
    <v-card-actions>
      <v-btn to="/chapel/checker" text>Go to Chapel Checker</v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { stringFormatDate } from '@/helpers/formatters'

function getTermText (term) {
  return (term.substring(4, 6) === '05' ? 'Spring ' : 'Fall ') + term.substring(0, 4)
}

export default {
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const term = ref('')
    const required = ref(0)
    const received = ref(0)
    const remaining = ref(0)
    const missed = ref(0)
    const makeups = ref(0)
    const makeupsAvailable = computed(() => missed.value - makeups.value)
    const nextMakeup = ref('')
    const missedDialog = ref(false)

    onMounted(async () => {
      const aggregate = [
        { $match: { pidm: user.value.pidm } },
        { $addFields: { received: { $sum: '$credits.credit' } } },
        { $project: {
          _id: 1,
          term: 1,
          required: 1,
          received: 1,
          remaining: { $max: [0, { $subtract: ['$required', '$received'] }] }
        } },
        { $sort: { term: -1 } }
      ]
      root.$feathers.service('chapel/student').find({ query: { aggregate } }).then(({ data }) => {
        if (data.length > 0) {
          term.value = getTermText(data[0].term)
          required.value = data[0].required
          received.value = data[0].received
          remaining.value = data[0].remaining
        }
      })
      loadMissed()
      loadMakeups()
    })

    async function loadMissed () {
      const { data: stuData } = await root.$feathers.service('chapel/student').find({ query: { pidm: user.value.pidm, $sort: { term: -1 } } })
      const term = stuData[0].term
      const chapels = stuData[0].credits.map(({ chapel }) => chapel)
      const aggregate = [
        { $addFields: { MWF: { $mod: [{ $dayOfWeek: '$date' }, 2] }, time: { $hour: { date: '$date', timezone: 'America/New_York' } } } },
        { $match: { term, $or: [{ isMakeup: false }, { isMakeup: { $exists: false } }], $expr: { $lte: ['$date', '$$NOW'] }, MWF: 0, time: 11, _id: { $nin: chapels } } }
      ]
      const { data } = await root.$feathers.service('chapel/schedule').find({ query: { aggregate } })
      missed.value = data.length
    }
    async function loadMakeups () {
      const { data: makeupChapels } = await root.$feathers.service('chapel/schedule').find({ query: { term: term.value, isMakeup: true, $limit: 50, $sort: { date: 1 } } })
      const chapelIds = makeupChapels.map(({ _id }) => _id)
      for (const { date } of makeupChapels) {
        if (new Date(date) > new Date()) {
          nextMakeup.value = stringFormatDate(date)
          break
        }
      }
      const aggregate = [
        { $match: { pidm: user.value.pidm, term: term.value } },
        { $unwind: '$credits' },
        { $match: { 'credits.chapel': { $in: chapelIds } } }
      ]
      const { data } = await root.$feathers.service('chapel/student').find({ query: { aggregate } })
      makeups.value = data.length
    }

    return {
      user,
      term,
      required,
      received,
      remaining,
      missed,
      makeups,
      makeupsAvailable,
      nextMakeup,
      missedDialog
    }
  }
}
</script>
